import { Component, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { CommonModule, NgClass, NgIf, DatePipe } from '@angular/common';
import { OverlayPanelModule, OverlayPanel } from 'primeng/overlaypanel';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { interval, Subscription, of } from 'rxjs';
import { switchMap, startWith } from 'rxjs/operators';
import { NotificationService } from '../../../shared/services/notification.service';
import { ViewNotificationDTO, NotificationListResponse } from '../../../shared/models/notification.model';
import { TokenStorageService } from '../../../core/auth/services/token-storage.service';
import { NotificationListDialogComponent } from '../../../shared/components/notification-list-dialog/notification-list-dialog.component';

@Component({
  selector: 'app-notification-bell',
  standalone: true,
  templateUrl: './notification-bell.component.html',
  styleUrls: ['./notification-bell.component.css'],
  imports: [CommonModule, NgClass, NgIf, OverlayPanelModule, ButtonModule, DatePipe, DividerModule, NotificationListDialogComponent]
})
export class NotificationBellComponent implements OnInit, OnDestroy {
  @ViewChild('op') op!: OverlayPanel;

  notifications: ViewNotificationDTO[] = [];
  isLoading = false;
  showAllNotificationsDialog = false;
  private pollingSubscription?: Subscription;
  private readonly POLLING_INTERVAL = 30000; // 30 secondi

  constructor(
    private notificationService: NotificationService,
    private tokenStorage: TokenStorageService
  ) {}

  ngOnInit(): void {
    // Il polling parte immediatamente grazie a startWith(0)
    this.startPolling();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  get unreadCount(): number {
    return this.notificationService.countUnread(this.notifications);
  }

  /**
   * Restituisce solo le prime 5 notifiche per il dropdown
   */
  get displayedNotifications(): ViewNotificationDTO[] {
    return this.notifications.slice(0, 5);
  }

  /**
   * Conta quante notifiche non lette ci sono oltre le prime 5
   */
  get remainingUnreadCount(): number {
    const displayedUnread = this.displayedNotifications.filter(n => !n.notification_is_checked).length;
    const totalUnread = this.unreadCount;
    return Math.max(0, totalUnread - displayedUnread);
  }

  /**
   * Verifica se ci sono altre notifiche oltre le prime 5
   */
  get hasMoreNotifications(): boolean {
    return this.notifications.length > 5;
  }

  /**
   * Avvia il polling per aggiornare le notifiche automaticamente
   */
  private startPolling(): void {
    const accountId = this.tokenStorage.getAccountId();
    if (!accountId) {
      console.warn('Account ID non trovato, impossibile caricare le notifiche');
      this.notifications = [];
      return;
    }

    let isFirstLoad = true;

    this.pollingSubscription = interval(this.POLLING_INTERVAL)
      .pipe(
        startWith(0), // Esegui immediatamente al primo avvio
        switchMap(() => {
          if (isFirstLoad) {
            this.isLoading = true;
          }
          const currentAccountId = this.tokenStorage.getAccountId();
          if (!currentAccountId) {
            // Se l'account ID non è più disponibile, restituisci un observable vuoto
            const emptyResponse: NotificationListResponse = { isSuccess: true, message: '', statusCode: 200, data: [] };
            return of(emptyResponse);
          }
          return this.notificationService.getNotifications({ AccountId: currentAccountId });
        })
      )
      .subscribe({
        next: (response) => {
          if (response.isSuccess && response.data) {
            this.notifications = response.data;
          } else {
            console.error('Errore nel caricamento delle notifiche:', response.message);
            // In caso di errore, mantieni le notifiche esistenti invece di svuotare l'array
          }
          if (isFirstLoad) {
            this.isLoading = false;
            isFirstLoad = false;
          }
        },
        error: (error) => {
          console.error('Errore nel polling delle notifiche:', error);
          if (isFirstLoad) {
            this.isLoading = false;
            isFirstLoad = false;
          }
          // In caso di errore di rete, mantieni le notifiche esistenti
        }
      });
  }

  /**
   * Ferma il polling
   */
  private stopPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }
  }

  /**
   * Apre/chiude il dropdown delle notifiche
   */
  openDropdown(event: Event): void {
    this.op.toggle(event);
  }

  /**
   * Segna una notifica come letta
   */
  markAsRead(notification: ViewNotificationDTO, event: Event): void {
    event.stopPropagation();
    
    if (notification.notification_id === null || notification.notification_is_checked) {
      return;
    }

    // Ottimisticamente aggiorna lo stato locale prima della chiamata API
    const previousState = notification.notification_is_checked;
    notification.notification_is_checked = true;

    this.notificationService.markAsRead(notification.notification_id)
      .subscribe({
        next: (response) => {
          if (response.isSuccess && response.data) {
            // Aggiorna con i dati dal server per sincronizzazione
            const index = this.notifications.findIndex(n => n.notification_id === notification.notification_id);
            if (index !== -1) {
              this.notifications[index] = response.data;
            }
          } else {
            // In caso di errore, ripristina lo stato precedente
            notification.notification_is_checked = previousState;
          }
        },
        error: (error) => {
          console.error('Errore nell\'aggiornamento della notifica:', error);
          // Ripristina lo stato precedente in caso di errore
          notification.notification_is_checked = previousState;
        }
      });
  }

  /**
   * Formatta la data della notifica in modo user-friendly
   */
  getNotificationDate(dateString: string): Date {
    return new Date(dateString);
  }

  /**
   * Apre il dialog con tutte le notifiche
   */
  openAllNotificationsDialog(): void {
    this.showAllNotificationsDialog = true;
    this.op.hide(); // Chiude il dropdown
  }

  /**
   * Gestisce l'evento quando una notifica viene segnata come letta nel dialog
   */
  onNotificationRead(notification: ViewNotificationDTO): void {
    // Aggiorna la notifica nella lista locale se presente
    const index = this.notifications.findIndex(n => n.notification_id === notification.notification_id);
    if (index !== -1) {
      this.notifications[index] = notification;
    }
  }
} 